import { Component, forwardRef, ViewChild, ChangeDetectionStrategy, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DistributionSelectorComponent } from '../distribution-selector/distribution-selector.component';
import { Distribution } from '../models';
import { DistributionService } from '../services';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'ep-distribution-selector-multiple',
    templateUrl: './distribution-selector-multiple.component.html',
    styleUrls: ['./distribution-selector-multiple.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DistributionSelectorMultipleComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DistributionSelectorComponent, AsyncPipe]
})
export class DistributionSelectorMultipleComponent implements ControlValueAccessor {
    @ViewChild('distributionSelector') distributionSelector: DistributionSelectorComponent;

    @Input() showFallbackDistributions = false;

    private distributionIds = new BehaviorSubject<number[]>([]);
    distributionIds$ = this.distributionIds.asObservable();

    distributions$ = this.distributionIds$.pipe(
        mergeMap(ids => this.loadDistributions(ids)),
        map(e => e.sort((a, b) => a.DocumentConfigIn.DistributionObject.Name > b.DocumentConfigIn.DistributionObject.Name ? 1 : -1))
    );

    disabled = false;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange = (_: any) => { };
    onTouched = () => { };

    constructor(
        private toastService: ToastService,
        private distributionService: DistributionService) {
    }

    async loadDistributions(distributionIds: number[]): Promise<Distribution[]> {
        try {
            return await this.distributionService.getDistributions(distributionIds);
        }
        catch (err) {
            console.log(err);
            this.toastService.showToast('Failed to load distributions', ToastType.error);
            return undefined;
        }
    }

    selectedDistributionIdChange(ev: any): void {
        // ev is null when selector component is reset
        if (ev) {
            const partnerId = ev as number;

            // Don't add duplicates
            if (!this.distributionIds.value.includes(partnerId)) {
                this.distributionIds.next([...this.distributionIds.value, partnerId]);
                this.onChange(this.distributionIds.value);
            }

            // Clear the partner selector
            this.distributionSelector.reset();
        }
    }

    remove(id: number): void {
        this.distributionIds.next(this.distributionIds.value.filter(e => e !== id));
        this.onChange(this.distributionIds.value);
    }

    // Begin ControlValueAccessor //

    writeValue(value: any): void {
        if (!value) {
            this.distributionIds.next([]);
        }
        else if (Array.isArray(value)) {
            this.distributionIds.next(value);
        }
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // End ControlValueAccessor //
}
